"use client";

import * as React from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { debounce } from "es-toolkit";

export interface City {
  id: string;
  name: string;
}

interface CitySelectProps {
  value?: City;
  onValueChange?: (value: City | undefined) => void;
  placeholder?: string;
  apiEndpoint: string;
  nationId?: string | number; // Accetta sia string che number
  disabled?: boolean;
}

export function CitySelect({
  value,
  onValueChange,
  placeholder = "Select city...",
  apiEndpoint,
  nationId,
  disabled = false,
}: CitySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [cities, setCities] = React.useState<City[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");
  const [hasCatalogCities, setHasCatalogCities] = React.useState<
    boolean | null
  >(null);

  // FIXED: Stato per mantenere l'oggetto città selezionato (evita che sparisca la label)
  const [selectedCity, setSelectedCity] = React.useState<City | undefined>();

  // FIXED: Debounce per la ricerca
  const debouncedSearch = React.useMemo(
    () =>
      debounce((val: string) => {
        setSearch(val);
      }, 300),
    [],
  );

  React.useEffect(() => {
    setHasCatalogCities(null);
    setSearch("");
    setInputValue("");
  }, [nationId]);

  React.useEffect(() => {
    // Se non c'è una nazione, non ha senso cercare città
    if (!nationId || nationId === 0 || nationId === "0") {
      setCities([]);
      setHasCatalogCities(null);
      return;
    }

    const fetchCities = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        params.append("countryId", nationId.toString());

        const response = await fetch(`${apiEndpoint}?${params.toString()}`);
        const data: City[] = await response.json();
        const list = Array.isArray(data) ? data : [];
        setCities(list);
        if (!search) {
          setHasCatalogCities(list.length > 0);
        }
      } catch (error) {
        if (!search) {
          setHasCatalogCities(false);
        }
      } finally {
        setLoading(false);
      }
    };

    // Aggiungo un piccolo delay per evitare chiamate multiple rapide
    const timeoutId = setTimeout(fetchCities, 100);
    return () => clearTimeout(timeoutId);
  }, [search, nationId, apiEndpoint]);

  React.useEffect(() => {
    if (value && value.name) {
      // Se il catalogo ha comuni (es. Italia), un comune con id "0" non è considerato valido
      if (hasCatalogCities === true && (value.id === "0" || !value.id)) {
        const matched = cities.find(
          (c) => c.name && c.name.toLowerCase() === value.name.toLowerCase()
        );
        if (matched) {
          setSelectedCity(matched);
          onValueChange?.(matched);
          return;
        }
      }

      const city = cities.find(
        (c) =>
          c.id === value.id ||
          (c.name &&
            value.name &&
            c.name.toLowerCase() === value.name.toLowerCase()),
      );
      setSelectedCity(city || value);
    } else {
      setSelectedCity(undefined);
    }
  }, [value, cities, hasCatalogCities]);

  // Se la nazione selezionata non ha comuni a catalogo nel DB (es. nazioni estere non censite),
  // mostra direttamente un campo di testo editabile.
  if (hasCatalogCities === false && nationId && nationId !== 0 && nationId !== "0") {
    return (
      <Input
        disabled={disabled}
        placeholder={placeholder}
        value={value?.name || ""}
        onChange={(e) => {
          const text = e.target.value;
          const customCity = text
            ? { id: "0", name: text }
            : undefined;
          setSelectedCity(customCity);
          onValueChange?.(customCity);
        }}
      />
    );
  }

  const trimmedInput = inputValue.trim();
  const allowCustomInput = hasCatalogCities === false;

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          debouncedSearch.cancel();
          setInputValue("");
          setSearch("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-transparent font-normal"
          // Disabilitato se non c'è una nazione selezionata
          disabled={disabled || !nationId || nationId === "" || nationId === 0}
        >
          <span className="truncate">
            {selectedCity?.name || value?.name || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[300px] p-0 max-w-[400px]"
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Cerca comune nel catalogo..."
            value={inputValue}
            onValueChange={(val) => {
              setInputValue(val);
              debouncedSearch(val);
            }}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty className="p-3 text-center text-xs">
                  <p className="text-muted-foreground mb-2">
                    {hasCatalogCities
                      ? "Nessun comune trovato nel catalogo. Digita per cercare o selezionane uno valido."
                      : "Nessuna città trovata."}
                  </p>
                  {allowCustomInput && trimmedInput.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full text-xs h-8 cursor-pointer font-normal justify-center"
                      onClick={() => {
                        const customCity: City = {
                          id: "0",
                          name: trimmedInput,
                        };
                        setSelectedCity(customCity);
                        onValueChange?.(customCity);
                        setOpen(false);
                      }}
                    >
                      Usa "{trimmedInput}"
                    </Button>
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {allowCustomInput &&
                    trimmedInput.length > 0 &&
                    !cities.some(
                      (c) =>
                        c.name?.toLowerCase() === trimmedInput.toLowerCase(),
                    ) && (
                      <CommandItem
                        value={`custom-${trimmedInput}`}
                        onSelect={() => {
                          const customCity: City = {
                            id: "0",
                            name: trimmedInput,
                          };
                          setSelectedCity(customCity);
                          onValueChange?.(customCity);
                          setOpen(false);
                        }}
                      >
                        Usa "{trimmedInput}"
                      </CommandItem>
                    )}
                  {cities.length > 0 &&
                    cities.map((city) => (
                      <CommandItem
                        key={city.id}
                        value={city.id}
                        onSelect={() => {
                          const isSelected = value && value.id === city.id;
                          const newCity = isSelected ? undefined : city;

                          setSelectedCity(newCity);
                          onValueChange?.(newCity);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value && value.id === city.id
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {city.name}
                      </CommandItem>
                    ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
